import { Severity, SpecHeaderType } from "node-library/dist/constants";
import {
	FrontendCompanySampleMatrix,
	FrontendCompanySampleSpecificationHeaders,
	FrontendCompanySampleSpecificationRow,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecificationDirectory,
} from "node-library/dist/portal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { sendPOSTRequest } from "../../requestHelper/sendRequest";
import { ButtonTextLoadingAnimation } from "../animation/ButtonLoadingAnimation";
import { GreenButton } from "../button";
import { CustomDropdown, CustomTextInput } from "../inputs";
import { RedErrorIcon } from "../toast/errorIcons";
import { useSnackbar } from "../toast/showToastMessage";
import { ModalWithButtons } from "./modal";
import { GreenTickIcon } from "../toast/successIcons";
import { overwriteFrontendCompanyInfo } from "../../helpers/companyHelper";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	selectedTestNode: FrontendCompanyTestHierarchyNode;
}

export default function AddSpecRowModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { open, setOpen, company, setCompany, selectedTestNode } = props;

	const [newRowName, setNewRowName] = useState<string>("");

	const [loading, setLoading] = useState<boolean>(false);
	useEffect(() => {
		if (open) {
			setNewRowName("");
		}
	}, [open]);

	const handleAddRow = async () => {
		if (!newRowName.trim()) {
			openSnackbar(
				translation("RowNameRequired"),
				Severity.ERROR,
				undefined,
				<RedErrorIcon />,
			);
			return;
		}

		setLoading(true);
		try {
			// Calculate the next display order
			const maxOrder = company.companySpecRows
				.filter((row) => row.hierarchyNodeId === selectedTestNode.nodeId)
				.reduce((max, h) => (h.displayOrder > max ? h.displayOrder : max), 0);

			const payload = {
				rowName: newRowName,
				testCategoryId: selectedTestNode.testCategoryId,
				hierarchyNodeId: selectedTestNode.nodeId,
				displayOrder: maxOrder + 1,
				// companyId is handled by backend 'additionalInfo', so usually not needed in body if extracted from token/session
				// otherwise add it here: companyId: company.companyId
			};

			// Replace '/specifications/create-header' with your actual API route
			const response = await sendPOSTRequest(
				"/spec-readings/create-specification-spec-row-v1",
				payload,
			);

			if (response.success) {
				openSnackbar(
					translation("RowCreated"),
					Severity.SUCCESS,
					undefined,
					<GreenTickIcon />,
				);
				const newRowId: string = response.data.newRowId;
				const newRow: FrontendCompanySampleSpecificationRow = {
					rowId: newRowId,
					testCategoryId: selectedTestNode.testCategoryId,
					hierarchyNodeId: selectedTestNode.nodeId,
					name: newRowName,
					displayOrder: maxOrder + 1,
				};
				overwriteFrontendCompanyInfo(
					{
						...company,
						companySpecRows: [...company.companySpecRows, newRow],
					},
					setCompany,
				);

				setOpen(false);
			}
		} catch (error) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	};

	const addSpecRowModalContent: React.ReactNode = (
		<div className="registration-input-content">
			<CustomTextInput
				id={"spec-row-name"}
				value={newRowName}
				setValue={setNewRowName}
				label={translation("RowName")}
				type={"text"}
				required
			></CustomTextInput>
		</div>
	);
	const addSpecFolderModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={translation("AddRow")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<GreenButton
					key="submit-create-folder-button"
					id="submit-create-folder-button"
					children={
						loading ? (
							<ButtonTextLoadingAnimation
								text={translation("Loading")}
							></ButtonTextLoadingAnimation>
						) : (
							translation("Create")
						)
					}
					onClick={handleAddRow}
					disabled={loading}
				></GreenButton>,
			]}
		>
			{addSpecRowModalContent}
		</ModalWithButtons>
	);
	return addSpecFolderModal;
}
